import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useOptInStore } from './store/optInStore';
import { useAuthStore } from './store/authStore';
import QuickUserGuideAutoShow from './QuickUserGuideAutoShow';

// Re-check the server once per page load: which agreement applies can change
// under a user (an admin assigns their company a new one), and the persisted
// store would otherwise keep waving them through until their next login.
let refreshedThisPageLoad = false;

/**
 * This component guards routes that require license agreement acceptance.
 * It first checks if the user is authenticated, then checks that they've
 * accepted the agreement that applies to them (per-company or default).
 */
const LicenseGuard = () => {
  const hasAcceptedLicense = useOptInStore(state => state.hasAcceptedLicense);
  const fetchLicenseStatus = useOptInStore(state => state.fetchLicenseStatus);
  const license = useOptInStore(state => state.license);
  const user = useAuthStore(state => state.user);
  const location = useLocation();
  const userEmail = user?.email || user?.id || '';
  const statusLoaded = Boolean(license && license.email === userEmail);
  // True once a server check for this user has finished (success or not).
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!userEmail || (statusLoaded && refreshedThisPageLoad)) return;
    refreshedThisPageLoad = true;
    fetchLicenseStatus(userEmail).finally(() => setChecked(true));
  }, [userEmail, statusLoaded, fetchLicenseStatus]);

  // Special handling for the license page itself to prevent loops
  if (location.pathname === '/license') {
    return <Outlet />;
  }

  // First check if user is authenticated
  if (!user) {
    // Avoid redirecting to login if we're already on the login page
    if (location.pathname === '/' || location.pathname === '/login') {
      return <Outlet />;
    }

    // If not logged in, redirect to login page
    return <Navigate to="/" replace />;
  }

  // Nothing cached for this user yet — wait for the server instead of
  // bouncing through /license.
  if (!statusLoaded && !checked) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  // Then check if they've accepted the license agreement
  if (!hasAcceptedLicense()) {
    return <Navigate to="/license" replace />;
  }

  // If authenticated and accepted license, allow access to protected routes.
  // The quick-users-guide popup rides alongside — it's purely informational
  // (unlike the license gate above, it never blocks the Outlet).
  return (
    <>
      <QuickUserGuideAutoShow />
      <Outlet />
    </>
  );
};

export default LicenseGuard;
